import { exists } from 'base/helpers';
import { Locale, LOCALE_INFO } from 'base/ui/locales';
import { normalizeSectionName } from 'base/webflow/page_component';
import { UndoManager } from 'blocks/woot_woot/undo';
import { mapLines } from '../../llm_output_parsers';
import { LLMProxyService } from '../../llm_proxy_service';
import {
  DEFAULT_HOMEPAGE_MIDDLE_SECTIONS,
  OLD_DEFAULT_PROJECT_WITHOUT_GLOBALS,
  OLD_DEFAULT_PROJECT_WITH_GLOBALS,
} from '../default_project';
import {
  createGlobal,
  globalSectionsInProject,
} from '../editing_actions/global_sections';
import {
  anInlineSection,
  aPage,
  aReferenceSection,
  aSection,
  Page,
  pageDomain,
  Project,
  SectionRefState,
  Space,
} from '../model';
import { findParentSpace } from '../model_helpers';
import { SelectionFactory } from '../selection/factory';
import { selectionLocale } from '../selection/store';
import {
  ApprovedCategories,
  LoadApprovedCategories,
} from './approved_categories';
import { TaskContext } from './task_log';

/* Inputs for the LLM shorter than this limit will be ignored. */
export const MIN_LLM_INPUT_LENGTH = 3;

/** Generates sections & subpage names for pages */
export class PageSectionsAi {
  constructor(
    private readonly llm: LLMProxyService,
    private readonly loadApprovedCategories: LoadApprovedCategories,
    private readonly project: Project,
    private readonly undo: UndoManager,
    private readonly selectionFactory: SelectionFactory,
  ) {}

  /** Generates a project home page, the home page sections & a list of subPage names */
  async generateProject(
    project: Project,
    space: Space | 'home',
    homePage: Page,
    pageCount: number,
    ctx: TaskContext,
  ) {
    if (!project.name) {
      const completion = this.llm.infer(
        'brief-to-project-title-1',
        {
          projectBrief:
            (space !== 'home' && space.description) || this.project.brief || '',
        },
        ctx,
      );
      for await (const line of mapLines(completion)) {
        if (line.trim() === 'DONE') break;
        const name = line.trim();
        if (name.length === 0) continue;
        project.name = name;
      }
    }

    const locale = selectionLocale({ space }, this.project);
    await this.getOrCreateGlobal('Navbar', locale).then((navbar) =>
      ctx.buffer.run(() => homePage.sections.push(navbar)),
    );
    const approvedCategories = await this.loadApprovedCategories();
    for (const isLastRetry of [false, false, true]) {
      const completion = this.llm.infer(
        'brief-to-homepage-3',
        {
          projectBrief:
            (space !== 'home' && space.description) || this.project.brief || '',
          pageCount,
          locale,
          languageLOTE: locale.startsWith('en')
            ? undefined
            : LOCALE_INFO[locale].name,
          sectionCategoryCsv:
            approvedCategories.forGeneratePage.createCsv(locale),
        },
        ctx,
      );
      const localHomeName =
        approvedCategories.homeTranslations.get(locale) || 'Home';
      let completionPart: 1 | 2 = 1;
      let pagesAddedByAI = 0;
      let sectionsAddedByAI = 0;
      for await (const line of mapLines(completion)) {
        if (line.includes('(2)')) completionPart = 2;
        if (line.trim() === 'DONE') break;
        if (completionPart === 1) {
          const pageTree = parsePagesCompletion(
            line.replace(/\(1\)/, ''),
            localHomeName,
          );
          for (const [name, subPages] of Object.entries(pageTree)) {
            ctx.buffer.run(() => {
              homePage.subPages.push(
                aPage({
                  name,
                  subPages: subPages.map((name) => aPage({ name })),
                }),
              );
            });
            pagesAddedByAI++;
          }
        } else {
          const cells = line.split('|').map((x) => x.trim());
          if (cells.length <= 1) continue;
          const [page = '', _id, name = '', description = ''] = cells;
          if (
            page.toLowerCase() === 'name' ||
            _id.toLowerCase() === 'category id'
          ) {
            // header
            continue;
          }
          const homeName = normalizeSectionName(
            approvedCategories.homeTranslations.get(locale) || 'Home',
          );
          if (!normalizeSectionName(page).includes(homeName)) {
            // The model should only give us the homepage.
            console.warn('Model wrote non-homepage section', {
              cells,
              homeName,
            });
            continue;
          }
          if (
            shouldIgnoreSection(
              { pageName: page, sectionName: name },
              approvedCategories,
              locale,
            )
          ) {
            continue;
          }
          const category = approvedCategories.forGeneratePage.findClosest(name);
          if (!category) {
            console.warn('Model wrote unapproved category', cells);
            continue;
          }
          ctx.buffer.run(() => {
            homePage.sections.push(anInlineSection({ name, description }));
          });
          sectionsAddedByAI++;
        }
      }
      if (pagesAddedByAI > 0 && sectionsAddedByAI > 0) {
        break;
      } else {
        // throw out what we currently have and try again
        const navbar = await this.getOrCreateGlobal('Navbar', locale);
        ctx.buffer.run(() => {
          homePage.subPages.clear();
          homePage.sections.clear();
          if (!isLastRetry) {
            homePage.sections.push(navbar);
          }
        });
        if (isLastRetry) {
          throw new Error('Model failed to add any pages or sections');
        }
      }
    }
    await this.getOrCreateGlobal('Footer', locale).then((footer) =>
      ctx.buffer.run(() => homePage.sections.push(footer)),
    );
  }

  /**
   * Generates the sections for a list of pages.  The caller should limit the number
   * of pages (to fit in the LLM's context window).
   **/
  async generatePageSections(
    pages: readonly Page[],
    ctx: TaskContext,
    onPageCompleted: (page: Page) => void,
    remainingTrys = 2,
  ) {
    if (pages.length === 0) return;
    const locales = pages.map((p) =>
      selectionLocale(this.selectionFactory.forPage(p), this.project),
    );
    const locale = locales[0];
    if (!locales.every((l) => l === locale)) {
      throw new Error('All pages must be in the same locale');
    }

    const navbar = await this.getOrCreateGlobal('Navbar', locale);
    ctx.buffer.run(() => {
      for (const page of pages) {
        page.sections.push(navbar);
      }
    });

    const space = this.selectionFactory.parentOfPage(pages[0])?.space;
    const homePage = findClosestHomepage(this.project, pages[0]);
    const sitePages = getAllPageNames(homePage)
      .filter(isUserWrittenName)
      .join(', ');
    const approvedCategories = await this.loadApprovedCategories();
    const completion = this.llm.infer(
      'page-completion-3',
      {
        projectBrief:
          (space !== 'home' && space.description) || this.project.brief || '',
        sitePages,
        ...createHomepageCsv(
          homePage,
          this.project,
          locale,
          approvedCategories,
        ),
        pagesToComplete: pages.map((p) =>
          p.description.trim()
            ? `${p.name} (additional instructions: ${p.description})`
            : p.name,
        ),
        locale,
        languageLOTE: locale.startsWith('en')
          ? undefined
          : LOCALE_INFO[locale].name,
        sectionCategoryCsv:
          approvedCategories.forGeneratePage.createCsv(locale),
      },
      ctx,
    );

    // The index into `pages` that we're currently completing
    let at = -1;
    // The page names as written by the AI
    let aiPageNames = Array.from({ length: pages.length }, () => '');
    let sectionsAddedByAI = Array.from({ length: pages.length }, () => 0);

    for await (const line of mapLines(completion)) {
      const [
        pageName = '',
        sectionId = '',
        sectionName = '',
        sectionDescription = '',
      ] = line.split('|').map((x) => x.trim());
      if (
        // skip header
        (pageName.toLowerCase() === 'page' &&
          sectionId.toLowerCase() === 'id') ||
        sectionName.toLowerCase().startsWith('section category') ||
        // skip blanks
        !pageName ||
        !sectionName ||
        shouldIgnoreSection(
          { pageName, sectionName },
          approvedCategories,
          locale,
        )
      ) {
        continue;
      }

      // advance to the next page if needed
      if (pageName !== aiPageNames[at]) {
        if (at >= 0) {
          const footer = await this.getOrCreateGlobal('Footer', locale);
          ctx.buffer.run(() => pages[at].sections.push(footer));
          onPageCompleted(pages[at]);
        }
        at++;
        if (at >= pages.length) {
          break; // completed all pages
        }
        if (pageName.toLowerCase() !== pages[at].name.toLowerCase()) {
          console.warn(
            'Model wrote wrong page name',
            { aiName: pageName, realName: pages[at].name },
            'continuing anyway (using index matches)',
          );
        }
        aiPageNames[at] = pageName;
      }

      // add the section
      const category =
        approvedCategories.forGeneratePage.findClosest(sectionName);
      if (!category) {
        console.warn('Model wrote unapproved category', line);
        continue;
      }
      ctx.buffer.run(() => {
        pages[at].sections.push(
          anInlineSection({
            name: sectionName,
            description: sectionDescription,
          }),
        );
      });
      sectionsAddedByAI[at]++;
    }

    if (at < pages.length && sectionsAddedByAI[at] > 0) {
      const footer = await this.getOrCreateGlobal('Footer', locale);
      ctx.buffer.run(() => pages[at].sections.push(footer));
      onPageCompleted(pages[at]);
    }

    const unwrittenPages = pages.filter((p, i) => sectionsAddedByAI[i] === 0);
    if (unwrittenPages.length > 0) {
      // If the model didn't add any sections, retry
      if (remainingTrys === 0) {
        const names = unwrittenPages.map((p) => p.name).join(', ');
        throw new Error(`Model failed to add any sections for ${names}`);
      }
      await this.generatePageSections(
        unwrittenPages,
        ctx,
        onPageCompleted,
        remainingTrys - 1,
      );
    }
  }

  async getOrCreateGlobal(
    nameEnglish: 'Navbar' | 'Footer',
    locale: Locale,
  ): Promise<SectionRefState> {
    const approvedCategories = await this.loadApprovedCategories();
    const category = approvedCategories.all.findClosest(nameEnglish);
    if (!category) throw new Error(`No category for ${nameEnglish}`);
    const name = category.translations.get(locale) || category.name;
    const nameNorm = normalizeSectionName(name);
    const { project } = this;
    for (const [id, { value }] of globalSectionsInProject(project)) {
      if (normalizeSectionName(value.name) === nameNorm) {
        return aReferenceSection({ id });
      }
    }
    return createGlobal(aSection({ name: name }), this.undo, project);
  }
}

function isNavbarOrFooter(
  sectionName: string,
  locale: Locale,
  approvedCategories: ApprovedCategories,
) {
  const ignoredNames = [
    'navbar',
    'footer',
    approvedCategories.all.findClosest('navbar')?.translations.get(locale),
    approvedCategories.all.findClosest('footer')?.translations.get(locale),
  ]
    .filter(exists)
    .map(normalizeSectionName);
  return ignoredNames.includes(normalizeSectionName(sectionName));
}

/**
 * Create the CSV needed by the prompt:
 *
 *   page|id|section category|description
 **/
function createHomepageCsv(
  home: Page,
  project: Project,
  locale: Locale,
  approvedCategories: ApprovedCategories,
): {
  homepageCsv: string;
  homepageName: string;
} {
  const output: string[] = [];
  const homepageName =
    approvedCategories.homeTranslations.get(locale) || 'Home';

  function maybeAddToOutput(sref: SectionRefState) {
    const section =
      sref.type === 'inline' ? sref.value : project.globalSections.get(sref.id);
    if (!section) return;
    if (!isUserWrittenName(section.name)) return;
    if (isNavbarOrFooter(section.name, locale, approvedCategories)) return;
    const category = approvedCategories.forGeneratePage.findClosest(
      section.name,
    );
    if (!category) return;
    output.push(
      `${homepageName}|${category.index1}|${section.name}|${section.description}`,
    );
  }

  for (const s of home.sections.state) {
    maybeAddToOutput(s);
  }
  const extraNeeded = Math.max(0, 6 - output.length);
  for (const s of DEFAULT_HOMEPAGE_MIDDLE_SECTIONS.slice(0, extraNeeded + 1)) {
    maybeAddToOutput(s);
  }
  return {
    homepageCsv: output.join('\n'),
    homepageName,
  };
}

/**
 * If a page is in a space new space (e.g. as a result of running `generateProject`),
 * using the "Homepage" in that space will provide better context than
 * `project.homePage`, which is probably still the default generic content.
 */
function findClosestHomepage(project: Project, page: Page): Page {
  const parentSpace = findParentSpace(project, page);
  if (parentSpace == null) {
    return project.homePage;
  }
  const pageRoot = findRootOfPage(project, page);
  if (pageRoot != null) {
    return pageRoot;
  }
  const spaceRoots = parentSpace.subPages.filter((p) => p !== page);
  const namedHome = spaceRoots.find((p) =>
    p.name.toLowerCase().includes('home'),
  );
  return namedHome || spaceRoots[0] || project.homePage;
}

function findRootOfPage(project: Project, inputPage: Page): Page | undefined {
  const parentSpace = findParentSpace(project, inputPage);
  if (parentSpace == null) {
    return;
  }

  for (const spaceRoot of parentSpace.subPages) {
    const q = [spaceRoot];
    while (q.length > 0) {
      const page = q.pop()!;
      if (page === inputPage) {
        return spaceRoot;
      }
      q.push(...page.subPages);
    }
  }
}

function getAllPageNames(page: Page): string[] {
  return [page.name, ...page.subPages.flatMap(getAllPageNames)];
}

// A full list of script codes can be found here: https://unicode.org/Public/UCD/latest/ucd/PropertyValueAliases.txt
const SHORT_SCRIPTS = [
  'Hang', // Hangul (Korean)  - e.g "홈" (literally home) is 1 code unit
  'Hani', // Han - e.g "主页" (home) is 2 code units
  'Hira', // Hiragana (Japanese)
  'Kana', // Katakana (Japanese)
  'Thai', // Thai
  'Yiii', // Yi
  'Newa', // Newa (Nepali)
  'Deva', // Devanagari (Hindi)
];

/** If a name field is acceptable as an input to the LLM */
export function isUserWrittenName(name: string): boolean {
  if (name.match(/^\s*page\s*\d+\s*$/i)) {
    return false; // Page 1 is the default name
  }
  if (name.length < MIN_LLM_INPUT_LENGTH) {
    const actualMin = SHORT_SCRIPTS.some((s) =>
      name.match(new RegExp(`\\p{Script=${s}}`, 'u')),
    )
      ? 1
      : 3;
    if (name.length < actualMin) {
      return false; // too short
    }
  }
  return true;
}

/**
 * Converts a completion like:
 *
 *    Home, About Us, B2B Services > B2B Service, Retail Trading, Blog > Blog Post, Products > Product, Contact Us
 *
 * Into a tree structure of pages.  Only 1 level of nesting is supported.
 */
export function parsePagesCompletion(
  line: string,
  localHomeName: string,
): Record<string, string[]> {
  const parts = line
    .split(',')
    .map((x) => x.trim())
    .filter((x) => {
      x = x.toLowerCase();
      if (x.length === 0) return false;
      if (x.includes('home')) return false;
      if (x.includes(localHomeName.toLowerCase())) return false;
      return true;
    });

  const output: Record<string, string[]> = {};
  for (const part of parts) {
    const [parentName, childName = ''] = part
      .split('>', 2)
      .map((x) => x.trim());
    const subPages = output[parentName] || [];
    if (childName.length > 0) subPages.push(childName);
    output[parentName] = subPages;
  }
  return output;
}

/**
 * Insert a new empty homepage, for the AI to generate a sitemap.
 *
 * Reuses the existing homepage if it is empty.
 **/
export function clearHomepage(
  project: Project,
  space: Space | 'home',
  homeTranslations: ReadonlyMap<Locale, string>,
): Page {
  const locale = selectionLocale({ space }, project);
  const name = homeTranslations.get(locale) || 'Home';

  let page;
  if (space === 'home') {
    page = project.homePage;
  } else {
    if (space.subPages.length === 0) {
      space.subPages.push(aPage({ name }));
    }
    page = space.subPages[0];
  }
  page.sections.clear();
  page.subPages.clear();
  page.name = name;
  return page;
}

/** If a space is empty, we say "Generate Sitemap" instead of "Regenerate Sitemap" */
export function spaceIsEmpty(project: Project, space: Space | 'home'): boolean {
  if (space === 'home') {
    const page = project.homePage;
    return (
      (page.sections.length === 0 && page.subPages.length === 0) ||
      pageDomain.statesEqual(
        project.homePage.state,
        OLD_DEFAULT_PROJECT_WITH_GLOBALS.homePage,
      ) ||
      pageDomain.statesEqual(
        project.homePage.state,
        OLD_DEFAULT_PROJECT_WITHOUT_GLOBALS.homePage,
      )
    );
  }

  if (space.subPages.length === 0) {
    return true;
  }
  if (space.subPages.length > 1) {
    return false;
  }
  const onlyPage = space.subPages[0];
  return onlyPage.sections.length === 0 && onlyPage.subPages.length === 0;
}

function shouldIgnoreSection(
  {
    sectionName,
    pageName,
  }: {
    sectionName: string;
    pageName: string;
  },
  approvedCategories: ApprovedCategories,
  locale: Locale,
) {
  const normSection = normalizeSectionName(sectionName);
  const normPage = normalizeSectionName(pageName);
  return (
    normSection.length === 0 ||
    isNavbarOrFooter(normSection, locale, approvedCategories) ||
    // We should translate this... Rip...
    // context: https://relume-io.slack.com/archives/C051JA82X3P/p1691107324148919
    (normPage === 'blog' && normSection === 'blog post header')
  );
}
